import { from, Observable, throwError } from "rxjs";
import { catchError, map, take, tap } from "rxjs/operators";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { nextRuntimeId } from "./runtime-id";
import { isPromise } from "./promise";
import { sntTranslate } from "src/app/support/translate";
import {
  NOTIFICATION_PRIORITY,
  NotificationService
} from "src/app/services/notification.service";
import { isFunction } from "lodash-es";
import { TabService } from "src/app/services/tab.service";
import { ToastService } from "src/app/services/toast.service";
import { HttpErrorResponse } from "@angular/common/http";
import { dateUtil } from "src/app/support/date-util";

import { sntLog } from "src/app/common/logger";
import { countBackendUserAction } from "src/app/support/sentry-metrics";

const logger = sntLog("NotifyDecorator");
export interface UserNotifyOptions {
  successMessage?: string | ((...args: any[]) => string);
  errorMessage?: string | ((...args: any[]) => string);
  progressMessage?: string;
  progressIndeterminate?: boolean;
  actions?: Array<{
    title: () => string;
    do: (providers: { tabService: TabService }, args: any) => void;
    variant?: "default" | "primary" | "neutral";
    outline?: boolean;
  }>;
  ignoreErrorCode?: number[] | undefined;
}

export interface UserObserveOptions {
  success?: string | ((...args: any[]) => string);
  error?: string | ((...args: any[]) => string);
  loading?: string | ((...args: any[]) => string);
}

export function UserNotify(options?: UserNotifyOptions) {
  return function (
    target: Object,
    key: string | symbol,
    descriptor: PropertyDescriptor
  ) {
    const originalValue: (...args: any[]) => Observable<any> = descriptor.value;

    descriptor.value = function (...args: any[]) {
      const className: string = this?.constructor?.name ?? "UnknownBackend";
      const methodName: string = String(key);
      const notificationId = nextRuntimeId();

      if (options?.progressIndeterminate) {
        const progressMessage = options?.progressMessage
          ? sntTranslate(options.progressMessage)
          : sntTranslate(marker("Operazione in corso"));

        NotificationService.instance.publish({
          id: notificationId,
          message: progressMessage,
          type: "indeterminate",
          priority: NOTIFICATION_PRIORITY.LOW,
          sent: dateUtil().toISOString()
        });
      }

      let originalObs: Observable<any> | Promise<any> = originalValue.apply(
        this,
        args
      );

      const wasPromise = isPromise(<Promise<any>>(<any>originalObs));

      if (wasPromise) {
        originalObs = from(originalObs);
      }

      const piped = originalObs.pipe(
        map((data) => {
          countBackendUserAction(className, methodName, "success");

          const notificationMessage: string | null = options?.successMessage
            ? getNotificationMessage(options!.successMessage, [...args, data])
            : null;

          if (notificationMessage) {
            NotificationService.instance.publish({
              id: notificationId,
              message: notificationMessage,
              type: "success",
              priority: NOTIFICATION_PRIORITY.LOW,
              sent: dateUtil().toISOString(),
              actions: options?.actions,
              actionsArgs: args
            });
          }

          return data;
        }),
        catchError((error) => {
          logger.error(error);
          countBackendUserAction(className, methodName, "error");

          const httpErrorResponse = error as HttpErrorResponse;

          const errorMessage = options?.errorMessage
            ? getNotificationMessage(options!.errorMessage, [error, ...args])
            : sntTranslate(marker("Errore"));

          if (!options?.ignoreErrorCode?.includes(httpErrorResponse.status)) {
            NotificationService.instance.publish({
              id: notificationId,
              message: errorMessage,
              type: "error",
              priority: NOTIFICATION_PRIORITY.LOW,
              sent: dateUtil().toISOString()
            });
          }

          return throwError(() => error);
        })
      );

      if (wasPromise) {
        piped.pipe(take(1)).subscribe();
      }

      return piped;
    };

    return descriptor;
  };
}

export function UserObserve(options: UserObserveOptions) {
  return function (
    target: Object,
    key: string | symbol,
    descriptor: PropertyDescriptor
  ) {
    const originalValue: (...args: any[]) => Observable<any> = descriptor.value;

    descriptor.value = function (...args: any[]) {
      const className: string = this?.constructor?.name ?? "UnknownBackend";
      const methodName: string = String(key);
      const notificationId = nextRuntimeId();

      let originalObs: Observable<any> = originalValue.apply(this, args);

      const promise = new Promise<void>((res, rej) => {
        originalObs = originalObs.pipe(
          tap(
            undefined,
            (error) => {
              countBackendUserAction(className, methodName, "error");
              rej(error);
            },
            () => {
              countBackendUserAction(className, methodName, "success");
              res();
            }
          )
        );
      });

      ToastService.instance.observe(() => promise, {
        error: (error: any) =>
          options.error
            ? getNotificationMessage(options.error, [...args, error])
            : sntTranslate(marker("Errore")),
        // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
        loading: getNotificationMessage(options.loading, args),
        // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
        success: (result: any) =>
          options.success
            ? getNotificationMessage(options.success, [...args, result])
            : null
      });

      return originalObs;
    };

    return descriptor;
  };
}

function getNotificationMessage(
  messageFactory: string | ((...args: any[]) => string),
  args: any[]
): string {
  if (isFunction(messageFactory)) {
    return messageFactory.apply(null, args);
  }

  return sntTranslate(messageFactory);
}
